import * as Placeholder from '-/Custom/Placeholder';
import { Stack, useRouter } from 'expo-router';
import { useMutation } from 'urql';
import ChatHomeListItem from '@/components/Chat/ChatHomeListItem';
import { ChatHomeListItemFragment } from '@/components/Chat/ChatHomeListItem.utils';
import {
  CHAT_LIST_PAGE_SIZE,
  GetChatHomePage,
  getChatCursorWhere,
} from '@/components/Chat/ChatListQuery';
import { SetChatHidden } from '@/components/Chat/ChatPersonPreference.utils';
import FooterActionButton from '@/components/Custom/FooterActionButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import ActionButton from '@/components/UI/ActionButton';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import List from '@/components/Unistyles/List';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import useCursorPagination from '@/hooks/useCursorPagination';
import useUserId from '@/hooks/useUserId';

function HiddenChatListItem({
  fragment,
  onUnhide,
  unhiding,
}: {
  fragment: FragmentOf<typeof ChatHomeListItemFragment>;
  onUnhide: (chatId: string) => Promise<void>;
  unhiding: boolean;
}) {
  const chat = readFragment(ChatHomeListItemFragment, fragment);
  const title = chat.metadata?.title || 'Untitled Chat';

  return (
    <ChatHomeListItem.Provider fragment={fragment}>
      <ChatHomeListItem.Row testID={`HiddenChat-${chat.id}`}>
        <ChatHomeListItem.Avatar />
        <ChatHomeListItem.Body>
          <ChatHomeListItem.Header>
            <ChatHomeListItem.Identity />
            <ChatHomeListItem.Status>
              <ChatHomeListItem.UnseenCount />
            </ChatHomeListItem.Status>
          </ChatHomeListItem.Header>
        </ChatHomeListItem.Body>
        <ActionButton
          accessibilityLabel={`Restore ${title}`}
          disabled={unhiding}
          forceLabel
          label="Restore"
          onPress={() => {
            void onUnhide(chat.id);
          }}
          testID={`RestoreHiddenChat-${chat.id}`}
          variant="secondary"
        />
      </ChatHomeListItem.Row>
    </ChatHomeListItem.Provider>
  );
}

export default function HiddenChats() {
  const { back } = useRouter();
  const userId = useUserId();
  const { data, error, fetching, fetchNextPage } = useCursorPagination({
    query: GetChatHomePage,
    baseVariables: {
      limit: CHAT_LIST_PAGE_SIZE,
      where: { is_hidden: { _eq: true } },
    },
    getNextPageVariables: (currentData) => {
      const lastChat = currentData?.chat_home.at(-1);
      if (!lastChat) {
        return null;
      }

      return {
        limit: CHAT_LIST_PAGE_SIZE,
        where: {
          is_hidden: { _eq: true },
          ...getChatCursorWhere(lastChat),
        },
      };
    },
  });
  const [{ error: unhideError, fetching: unhiding }, setChatHidden] =
    useMutation(SetChatHidden);
  const chatPage = data?.chat_home ?? [];
  const chats = chatPage.filter(
    (chat) => readFragment(ChatHomeListItemFragment, chat).is_hidden === true,
  );

  const unhideChat = async (chatId: string) => {
    await setChatHidden({
      chatId,
      isHidden: false,
      personId: userId,
    });
  };

  return (
    <Box flex>
      <Stack.Screen
        options={{
          title: 'Hidden Chats',
        }}
      />

      <List
        data={chats}
        fetching={fetching}
        keyExtractor={(item) => readFragment(ChatHomeListItemFragment, item).id}
        keyboardScroll={false}
        onEndReached={fetchNextPage}
        onEndReachedThreshold={0.5}
        onStartReachedThreshold={0}
        renderItem={({ item }) => (
          <HiddenChatListItem
            fragment={item}
            onUnhide={unhideChat}
            unhiding={unhiding}
          />
        )}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon
              name={error ? 'alert-circle-outline' : 'eye-off-outline'}
            />
            <Placeholder.Text>
              {error
                ? 'Unable to load hidden chats.'
                : 'You have no hidden chats.'}
            </Placeholder.Text>
            {!error ? (
              <Placeholder.Text>
                Chats you hide will appear here.
              </Placeholder.Text>
            ) : null}
          </Placeholder.Container>
        }
      />

      {unhideError ? (
        <Text center px="lg" size="sm" variant="error">
          Unable to restore that chat. Please try again.
        </Text>
      ) : null}
      <ModalKeyboardStickyView>
        <FooterActionButton
          label="Done"
          testID="FormSaveButton"
          onPress={back}
        />
      </ModalKeyboardStickyView>
    </Box>
  );
}
